import { ReactNode, Component, PureComponent, CSSProperties, ReactElement } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { WidgetLocalizationKeys, PartialIWidgetThingsProps, IconURIPath } from '../WidgetsThingsStructs';
import { DashboardManagerParent } from '@UI/Dashboard/DashboardManagerParent';
import { isWebThingKey, StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { _ITrackUpdates } from './DynamicPage';
import { TitleAlign, WidgetTitleStyle, IOutline, ITrackUpdates, ITsViewFormat } from '@ModelViews/ModelViews';
import Factory from '@UI/Widgets/Factory';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { DummyWebNode } from '../Utils/DummyWebNode';
import { IWidgetClassParent } from '@UI/Widgets/IWidgetClassParent';
import { eIconType, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { Canvas, CanvasButtonEvent, CanvasItemDescDictionary, CanvasItemLayoutDescDictionary, CanvasLayout, CanvasStaticItem } from '@UI/Canvas/Canvas';
import { IWidgetDescription, IWidgetDictionary, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebModule } from '@API/WebModule';
import { getRandomSmallKey, isDefined, IsNullOrEmpty, isString } from '@Utils/Micro';
import { getRowMinHeight } from '@Primitive/Typography';
import { ToolbarIconButton, ToolbarIconButtonProps } from '@Primitive/ToolbarIconButton';
import { RenderToolbar } from '@UI/Layout/AdvanceLayout';
import { PlegmaStatics } from '@API/PlegmaStatics';
import { Plegma } from '@API/Api';
import { DateFormat } from '@Utils/Formats';
import { SettingsList } from '@Primitive/SettingsList';
import { WebThing, WebThingPort } from '@API/WebThing';
// =================================================================================================
export interface DynamicGridData {
    /**
     * Widget's width in pixels
     */
    WidgetWidth: number;

    /**
     * Widget's height in pixels
     */
    WidgetHeight: number;

    /**
     * The margin of the widgets in the grid
     */
    WidgetMargin: { w: number, h: number }

    /**
     * Whether to stretch widgets height to fill available space or not.
     */
    FillHeight: boolean;

    /**
     * Widget's background color
     */
    BackgroundColor: string;

    /**
     * Widget's box shadow
     */
    ZDepth: number;

    /**
     * Widget's border radius
     */
    BorderRadius: number;

    /**
     * Settings for title in widgets' toolbar.
     */
    TitleStyle: WidgetTitleStyle;

    /**
     * Whether to show 'zoom button' in toolbar or not.
     */
    Zoom: boolean;

    /**
     * Widget's outline
     */
    Outline: IOutline;

    /**
     * Custom toolbar icons
     */
    ToolbarIcons: ToolbarIcon[];

    /**
     * Customizes the tracking of view updates.
     */
    TrackUpdates: ITrackUpdates;

    /**
     * Settings for title in groups' toolbar.
     */
    GroupTitleStyle: SetupGroupStyle;

    /**
     * Settings for groups before/after margins
     */
    GroupMargin: {
        Before: number;
        After: number;
    }

    /**
     * Whether to support ordering in groups items
     */
    GroupsOrdering: boolean;
}

interface ToolbarIcon {
    /**
     * Icon's unique ID
     */
    id: string;

    /**
     * Icon's tooltip
     */
    tooltip?: string;

    /**
     * Icon
     */
    icon?: string;

    /**
     * Icon type
     */
    iconType?: eIconType;

    /**
     * Optional color when icon type is font-awesome.
     */
    iconColor?: string;
}
// -------------------------------------------------------------------------------------------------
function SetupJsonSchemeValidator() {
    const toolbarIconsAjvSchema = {
        type: 'array',
        items: {
            type: 'object',
            properties: {
                id: { type: 'string' },
                tooltip: { type: 'string' },
                icon: { type: 'string' },
                iconType: { enum: [0, 1, 2] },
                iconColor: { type: 'string' },
            },
            required: ['id']
        }
    };
    return JsonSchemaValidator.GetInstance('SetupJsonSchemeValidator', {
        title: 'SetupJsonSchemeValidator',
        type: 'object',
        properties: {
            width: { type: 'number' },
            height: { type: 'number' },
            widgets: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        id: { type: ['number', 'string'] },
                        type: {
                            enum: [
                                // Charts category.
                                'barchart',
                                'piechart',
                                'doughnutchart',
                                'polarareachart',
                                'linechart',
                                'bubblechart',

                                // Gauges category.
                                'radialgauge',
                                'lineargauge',
                                'knob',
                                'gaugelabel',
                                'percentagegauge',

                                // View category.
                                'infotip',
                                'maps',
                                'markdown',
                                'tile',
                                'stattile',

                                // Singleton category.
                                'buttonsingleton',

                                // Image category.
                                'imageviewer'
                            ]
                        },
                        settings: { type: 'object' },
                        title: { type: 'string' },
                        toolbarIcons: toolbarIconsAjvSchema,
                        trackUpdates: {
                            type: 'object',
                            properties: {
                                enable: { type: 'boolean' },
                                timestamp: { type: 'string' },
                                isExplicit: { type: 'boolean' },
                                dateFormat: { enum: [0, 1, 2, 3] },
                                viewFormat: { enum: [0, 1, 2] }
                            }
                        },
                        isStatic: { type: "boolean" },
                        beginInLoadingState: { type: 'boolean' }
                    },
                    required: ['id', 'type']
                }
            },
            toolbarIcons: toolbarIconsAjvSchema,
            groups: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        widgets: {
                            type: 'array',
                            items: { type: ['number', 'string'] }
                        },
                        title: { type: 'string' },
                        titleStyle: {
                            type: 'object',
                            properties: {
                                alignment: { enum: ['left', 'center', 'right'] },
                                backgroundColor: { type: 'string' },
                                fontColor: { type: 'string' },
                                icon: { type: 'string' },
                                titleHeight: { type: 'number' },
                            }
                        },
                    }
                }
            }
        }
    });
}

function DataJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance('DataJsonSchemeValidator', {
        title: 'DataJsonSchemeValidator',
        type: 'array',
        items: {
            type: 'object',
            properties: {
                id: { type: ['number', 'string'] },
                data: { type: ['number', 'string', 'object', 'array', 'boolean'] },
                timestamp: { type: 'string' }
            },
            required: ['id', 'data']
        }
    });
}

interface Setup {
    /**
     * Groups
     */
    groups: SetupGroup[];

    /**
     * Widgets
     */
    widgets: SetupWidget[];

    /**
     * Widgets' width in pixels
     */
    width: number;

    /**
     * Widget's height in pixels
     */
    height: number;

    /**
     * Toolbar icons
     */
    toolbarIcons: ToolbarIcon[];
}

interface SetupGroup {

    /**
     * Group title
     */
    title?: string;

    /**
     * Settings for title in widgets' toolbar.
     */
    titleStyle?: SetupGroupStyle;

    /**
     * Group's widgets
     */
    widgets?: (number | string)[];

    // todo: in the future, add more fields about grid settings :)
}

interface SetupGroupStyle {
    alignment: TitleAlign;
    backgroundColor: string;
    fontColor: string;
    icon: string;
    titleHeight: number;
}

interface SetupWidget {
    /**
     * Widget UID
     */
    id: number | string;

    /**
     * Widget's type
     */
    type: string;

    /**
     * Widget's settings
     */
    settings: any;

    /**
     * Widget's title
     */
    title: string;

    /**
     * Toolbar icons
     */
    toolbarIcons: ToolbarIcon[];

    /**
     * Customizes the tracking of view updates.
     */
    trackUpdates: _ITrackUpdates;

    /**
    * Used at reordering. If enabled the widget cannot be moved. Also, its index remains the same.
    */
    isStatic: boolean;

    /**
     * Displays a loading indicator while waiting for data.
     */
    beginInLoadingState: boolean;
}

interface Data {
    id: number | string;
    data: any;
    timestamp: string;
}

interface SelectedToolbarIconJSON {
    /**
     * Current selected widget.
     */
    widget: SetupWidget;

    /**
     * Current selected icon.
     */
    icon: ToolbarIcon;
}

interface WidgetDataJSON {
    /**
     * Current selected widget.
     */
    widget: SetupWidget;

    /**
     * Triggered port key
     */
    portKey: string;

    /**
     * Port data
     */
    data: any;
}


interface GroupReorderJson {
    /**
     * Current group.
     */
    group: SetupGroup;

    /**
     * Widgets order.
     */
    widgetsOrderIds: (number | string)[];
}

// -------------------------------------------------------------------------------------------------
export interface DynamicGridState {
    widgetWidth: number;
    widgetHeight: number;
    toolbarIcons: ToolbarIcon[];
    groups: SetupGroup[];
    groupCanvas: {
        groupIndex: number;
        hasChanges: boolean;
        canvasLayout: CanvasLayout;
        originalLayoutDesc: CanvasItemLayoutDescDictionary;
    }
}
// -------------------------------------------------------------------------------------------------
class DynamicGrid extends Component<PartialIWidgetThingsProps<DynamicGridData>, DynamicGridState> {
    // ---------------------------------------------------------------------------------------------
    private static _WidgetDescriptors: { [key: string]: IWidgetDescription };
    private static _AllowedWidgetTypes: string[];
    private static _FriendlyTypeToWidgetType: { [key: string]: string };
    // ---------------------------------------------------------------------------------------------
    private _webNode: WebNode;
    private _webModule: WebModule;
    private _widgetsSetup: { [key: string]: SetupWidget; };
    private _widgets: IWidgetDictionary;
    private _widgetsOrder: (number | string)[];
    private _previousVersions: { [portKey: string]: number };
    // ---------------------------------------------------------------------------------------------
    constructor(props: PartialIWidgetThingsProps<DynamicGridData>) {
        super(props);

        // initialize static variables (if needed)
        if (!isDefined(DynamicGrid._AllowedWidgetTypes)) {
            DynamicGrid._AllowedWidgetTypes = [
                // Charts category.
                'BarChartWidget',
                'PieChartWidget',
                'DoughnutChartWidget',
                'PolarAreaChartWidget',
                'LineChartWidget',
                'BubbleChartWidget',

                // Gauges category.
                'RadialGaugeWidget',
                'LinearGaugeWidget',
                'KnobWidget',
                'GaugeLabelWidget',
                'PercentageGaugeWidget',

                // View category.
                'MapsWidget',
                'InfoTipCoreWidget',
                'MarkdownWidget',
                'TileWidget',
                'StatTileWidget',

                // Singleton category.
                'ButtonSingletonWidget',

                // Image category.
                'ImageViewerWidget'
            ];

            DynamicGrid._FriendlyTypeToWidgetType = {
                // Charts category.
                'barchart': 'BarChartWidget',
                'piechart': 'PieChartWidget',
                'doughnutchart': 'DoughnutChartWidget',
                'polarareachart': 'PolarAreaChartWidget',
                'linechart': 'LineChartWidget',
                'bubblechart': 'BubbleChartWidget',

                // Gauges category.
                'radialgauge': 'RadialGaugeWidget',
                'lineargauge': 'LinearGaugeWidget',
                'knob': 'KnobWidget',
                'gaugelabel': 'GaugeLabelWidget',
                'percentagegauge': 'PercentageGaugeWidget',

                // View category.
                'maps': 'MapsWidget',
                'infotip': 'InfoTipCoreWidget',
                'markdown': 'MarkdownWidget',
                'tile': 'TileWidget',
                'stattile': 'StatTileWidget',

                // Singleton category.
                'buttonsingleton': 'ButtonSingletonWidget',

                // Image category.
                'imageviewer': 'ImageViewerWidget'
            }

            DynamicGrid._WidgetDescriptors = {};
            _.forEach(DynamicGrid._AllowedWidgetTypes, (type) => {
                DynamicGrid._WidgetDescriptors[type] = Factory.CreateWidgetThingDescDefault(type);
            });
        }

        this._widgetsSetup = {};
        this._widgets = {};
        this._widgetsOrder = [];

        // get dummy webnode and dummy webmodule
        // keep all widgets under a specific WebModule
        const dummyWebNodeRes = DummyWebNode.getDummyWebNodeAndWebModule(
            this.props.ThingKey,
            this._dummyPortEventOutCB,
            'dynamic_grid_dummy_node_key'
        );

        this._webNode = dummyWebNodeRes.webNode;
        this._webModule = dummyWebNodeRes.webModule;
        // disable session based mode
        this._webModule.SetSessionBasePorts(false);

        // try get widget's parent
        const widgetsParent = DashboardManagerParent.GetInstance(this.props.NodeKey);

        // create dynamic widgets' parent; try pass values from current widget's parent
        const parent = new IWidgetClassParent(
            // _CheckForUpdatedData
            (Key: string, NewWidgetData: any) => { },
            // _inEditMode
            isDefined(widgetsParent) ? () => widgetsParent.Info.inEditMode() : () => false,
            // _supportEditing
            isDefined(widgetsParent) ? widgetsParent.Info.SupportEditing : false,
            // _inDebug
            isDefined(widgetsParent) ? () => widgetsParent.Info.inDebug() : () => false,
            // _dialogConf
            isDefined(widgetsParent) ? widgetsParent.Info.DialogConf : null,
            { showCopy: true }
        );

        // link the parent backreference to this dialog
        let parentInfo = DashboardManagerParent.GetInstance(this._webNode.NodeKey);
        if (!isDefined(parentInfo))
            parentInfo = new DashboardManagerParent(this._webNode.NodeKey, parent);
        else
            parentInfo.Info = parent;

        this.state = this._getStateFromProps(this.props);
    }
    // ---------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: PartialIWidgetThingsProps<DynamicGridData>) {
        // check if widget data was changed
        if (!_.isEqual(this.props.WidgetData, prevProps.WidgetData)) {
            this.setState(this._getStateFromProps(this.props));
        }

        // are ports touched?
        if (this.props.PortVersion != prevProps.PortVersion) {
            this._updateData(this.props);
        }
    }
    // ---------------------------------------------------------------------------------------------
    componentDidMount() {
        this._previousVersions = {};
        this._updateData(this.props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        // get fields from props & state
        const { width, height, WidgetData } = this.props;
        const { Outline, WidgetMargin,
            TitleStyle, GroupTitleStyle, Zoom,
            FillHeight,
            ZDepth, BackgroundColor, BorderRadius,
            GroupMargin,
            GroupsOrdering,
            TrackUpdates
        } = WidgetData;
        const { widgetWidth, widgetHeight, toolbarIcons, groups, groupCanvas } = this.state;

        // either use groups from port or wrap all widgets into 1 group
        const _groups: SetupGroup[] = isDefined(groups) ? groups :
            [{
                title: null,
                titleStyle: GroupTitleStyle,
                widgets: this._widgetsOrder,
            }];

        // calculate widget & container width/height
        const widgetContainerWidth = widgetWidth + 2 * Outline.Thickness + WidgetMargin.w;

        // widget title is not included in widget height, it is considered as an offset
        // todo: investigate if this approach is ok
        // check if toolbar will be shown
        // either zoom is enabled or at least 1 widget has title
        const showTitle = Zoom || isDefined(_.find(this._widgets, (widget) => !IsNullOrEmpty(widget.Description.WidgetTitle)));

        let widgetHeightOffset = 2 * Outline.Thickness;
        let widgetContainerHeight = widgetHeight + widgetHeightOffset + (showTitle ? TitleStyle.TitleHeight : 0);

        // calculate how many widgets fit horizontally
        const columnsLength = Math.floor(width / widgetContainerWidth);

        // fix height if `FillHeight` is enabled
        if (FillHeight) {

            // calculate:
            //  height for groups
            //  # of used widgets
            //  # of groups
            let availableHeight = height;
            let groupsCnt = 0;

            const widgetsCnt = _.sum(
                _.map(_groups,
                    (group, index) => {
                        groupsCnt++;

                        const groupTitleStyle: SetupGroupStyle = _.defaults({}, group.titleStyle, GroupTitleStyle);
                        if (this._drawGroupTitle(group.title, groupTitleStyle)) {
                            availableHeight -= groupTitleStyle.titleHeight + GroupMargin.After;
                            if (groupsCnt > 1)
                                availableHeight -= GroupMargin.Before;
                        }
                        return isDefined(group.widgets) ? group.widgets.length : 0
                    }
                )
            );

            // check if we available height
            if (availableHeight > 0) {

                // calculate # of rows
                let widgetRows = Math.ceil(widgetsCnt / columnsLength);

                // if more than 1 row; increase # of rows
                if (groupsCnt > 1)
                    widgetRows += groupsCnt - 1;

                // calculate height
                widgetContainerHeight = getRowMinHeight(
                    widgetRows,
                    // available height
                    availableHeight,
                    // overall widget height (WidgetData + setup port)
                    widgetContainerHeight,
                    // total padding
                    widgetRows > 1 ? ((widgetRows - 1) * WidgetMargin.h) : 0);
            }
        }

        // calculate widget's height (remove vertical offset)
        const _widgetHeight = widgetContainerHeight - widgetHeightOffset;

        const canvasItemSettings = {
            zDepth: ZDepth,
            BackgroundColor: BackgroundColor,
            NoBackground: false,
            BorderRadius: BorderRadius,
            Outline: Outline
        };

        const renderGridItem = (widgetID, index) => {

            // try get widget
            const widget = this._widgets[widgetID];
            if (!isDefined(widget))
                return;

            let desc = widget.Description;
            const key = desc.Key;

            // try get toolbar buttons from setup
            const setup = this._widgetsSetup[key];
            let _toolbarIcons = setup.toolbarIcons;
            if (!isDefined(_toolbarIcons))
                // fallback to global toolbar buttons
                _toolbarIcons = toolbarIcons;

            // Set the tracking of view updates.
            const _trackUpdates = setup.trackUpdates;
            desc.TrackUpdates = isDefined(_trackUpdates)
                ? {
                    ShowLastUpdateTs: _trackUpdates.enable,
                    DateFormat: _trackUpdates.dateFormat,
                    ViewFormat: _trackUpdates.viewFormat,
                    Timestamp: _trackUpdates.timestamp,
                    IsExplicit: _trackUpdates.isExplicit
                } : TrackUpdates;

            return <div
                key={'grid-cell-' + index}
                style={{
                    height: widgetContainerHeight,
                    position: 'relative',
                    paddingTop: Outline.Thickness,
                    paddingLeft: Outline.Thickness,
                    margin: 2
                }}
            >
                <CanvasStaticItem
                    width={widgetWidth}
                    height={_widgetHeight}
                    ItemDesc={{
                        Key: key,
                        Title: desc.WidgetTitle,
                        ToolbarBackgroundColor: TitleStyle.BackgroundColor,
                        ToolbarFontColor: TitleStyle.FontColor,
                        ToolbarIcon: TitleStyle.Icon,
                        Alignment: TitleStyle.Alignment,
                        TitleHeight: TitleStyle.TitleHeight,
                        ShowExtraMenu: Zoom,
                        Render: (k, w, h) => widget.RenderBase(k, w, h),
                        RenderToolbar: (k, toolbarHeight) => {

                            // check toolbar icons are specified
                            if (!isDefined(_toolbarIcons) || _toolbarIcons.length == 0)
                                return widget.RenderBaseToolbar(k, toolbarHeight);

                            return _.concat(_.map(_toolbarIcons, (iconInfo) => {

                                const iconProps: ToolbarIconButtonProps = {
                                    iconType: iconInfo.iconType,
                                    style: {
                                        color: iconInfo.iconColor,
                                        marginLeft: 10,
                                        cursor: 'pointer'
                                    }
                                };

                                if (iconInfo.iconType == eIconType.fontAwesome)
                                    iconProps.className = iconInfo.icon;
                                else {
                                    iconProps.src = iconInfo.icon;
                                    iconProps.height = Math.ceil(TitleStyle.TitleHeight * 0.8);
                                    iconProps.style.paddingBottom = Math.ceil(TitleStyle.TitleHeight * 0.05);
                                }

                                return <ToolbarIconButton
                                    id='icons'
                                    key={'icon-' + iconInfo.id}
                                    tooltip={iconInfo.tooltip}
                                    onClick={() => {
                                        // send port event
                                        this.props.SendPortEvent(PortKeys.toolbarIcon, JSON.stringify({
                                            icon: iconInfo,
                                            widget: setup
                                        } as SelectedToolbarIconJSON));

                                    }}
                                    {...iconProps}
                                />
                            }) as ReactNode[],
                                widget.RenderBaseToolbar(k, toolbarHeight)
                            );
                        },
                        ToolbarDynamicTooltip: () => widget.ToolbarDynamicTooltip(),
                        IsHidden: false,
                    }}
                    doNotRedraw={false}
                    ElementSettings={canvasItemSettings}
                />
            </div>
        }

        return <div key='main-div'>
            {
                _.map(_groups, (group, index) => {
                    // check if we need to render a title
                    let titleJSX: ReactElement<any> = null;

                    const { title, titleStyle } = group;
                    //  override style from port
                    const groupTitleStyle: SetupGroupStyle = _.defaults({}, titleStyle, GroupTitleStyle);

                    const renderCanvas = groupCanvas != null && index == groupCanvas.groupIndex;

                    // need to draw a toolbar?
                    if (this._drawGroupTitle(title, groupTitleStyle)) {
                        const groupStyle: CSSProperties = {
                            marginTop: index != 0 ? GroupMargin.Before : 0,
                            marginBottom: GroupMargin.After,
                            background: groupTitleStyle.backgroundColor,
                            color: groupTitleStyle.fontColor
                        };

                        let elements = [
                            IsNullOrEmpty(groupTitleStyle.icon) ? null :
                                <ToolbarIconButton
                                    id='icons'
                                    className={groupTitleStyle.icon}
                                    style={{ cursor: 'default' }}
                                />,
                            <div style={{ paddingLeft: 5 }}>{title}</div>
                        ];
                        // based on alignment, decide which elements to define
                        let leftElements: ReactNode[] = null,
                            centerElements: ReactNode[] = null,
                            rightElements: ReactNode[] = null;
                        switch (groupTitleStyle.alignment) {
                            case TitleAlign.Center:
                                centerElements = elements;

                                break;
                            case TitleAlign.Left:
                                leftElements = elements;
                                groupStyle.paddingLeft = 10;
                                break;
                            case TitleAlign.Right:
                                groupStyle.paddingRight = 10;
                                rightElements = elements;
                                break;
                        }

                        // fill right elements with buttons? 
                        if (GroupsOrdering && isDefined(group.widgets) && group.widgets.length > 0) {

                            if (!isDefined(rightElements))
                                rightElements = [];

                            if (!renderCanvas)
                                rightElements.push(<ToolbarIconButton
                                    id='icons'
                                    key='change-layout'
                                    className='fas fa-th-large'
                                    tooltip='Change layout'
                                    onClick={() => {

                                        const itemDesc: CanvasItemDescDictionary = {};
                                        const layoutDesc: CanvasItemLayoutDescDictionary = {};
                                        _.forEach(group.widgets,
                                            // hack: add 'any' to skip typescript error
                                            (w: any, ii) => {

                                                // try get widget
                                                const widget = this._widgets[w];
                                                if (!isDefined(widget))
                                                    return;

                                                const key = widget.Description.Key;

                                                // try get toolbar buttons from setup
                                                const setup = this._widgetsSetup[key];
                                                const isStatic = setup.isStatic == true;

                                                itemDesc[w] = {
                                                    Key: w,
                                                    Render: (k, ww, hh) => { return renderGridItem(w, ii); }
                                                }
                                                layoutDesc[w] = {
                                                    Key: w,
                                                    IsResizable: false,
                                                    IsDraggable: !isStatic,
                                                    IsStatic: isStatic,
                                                    H: 1,
                                                    W: 1,
                                                    X: ii % columnsLength,
                                                    Y: Math.floor(ii / columnsLength)
                                                }
                                            });

                                        this.setState({
                                            groupCanvas: {
                                                canvasLayout: {
                                                    LayoutDesc: layoutDesc,
                                                    ItemDesc: itemDesc
                                                },
                                                groupIndex: index,
                                                hasChanges: false,
                                                originalLayoutDesc: _.cloneDeep(layoutDesc)
                                            }
                                        })

                                    }}
                                />)
                            else {
                                // add save/cancel buttons
                                if (groupCanvas.hasChanges) {
                                    rightElements.push(
                                        <ToolbarIconButton
                                            id='icons'
                                            key='save-btn'
                                            className='fas fa-upload'
                                            tooltip='Save changes'
                                            onClick={this.onGroupReorderSave}
                                        />
                                    )
                                }
                                rightElements.push(
                                    <ToolbarIconButton
                                        id='icons'
                                        key='cancel-btn'
                                        className='fas fa-times'
                                        tooltip='Discard changes'
                                        onClick={this.onGroupReorderCancel}
                                    />
                                );
                            }
                        }
                        titleJSX = <RenderToolbar
                            key={'row-title-' + index}
                            LeftElements={leftElements}
                            CenterElements={centerElements}
                            RightElements={rightElements}
                            height={groupTitleStyle.titleHeight}
                            style={groupStyle}
                        />;
                    }

                    let gridJSX: ReactElement<any> = null;

                    if (renderCanvas) {

                        let groupsRows = Math.ceil(group.widgets.length / columnsLength);

                        gridJSX = <Canvas

                            width={width}
                            InEditMode={true}
                            // hack height assignment in Canvas element :P
                            height={(widgetContainerHeight * groupsRows) / 2}
                            rootStyle={{ height: widgetContainerHeight * groupsRows }}

                            WidthPixels={widgetWidth}
                            HeightPixels={_widgetHeight}
                            SizeW={columnsLength}

                            CompactLayout={false}
                            GridLayout={false}
                            FillHorizontalGaps={true}

                            onCanvasLayoutChanged={this._onCanvasLayoutChanged}
                            toolbarButtonsToHide={[
                                CanvasButtonEvent.Copy,
                                CanvasButtonEvent.Settings,
                                CanvasButtonEvent.Hide,
                                CanvasButtonEvent.Delete
                            ]}
                            Margin={{
                                H: 3,//WidgetMargin.h,
                                W: 3,//WidgetMargin.w
                            }}
                            Padding={0}
                            WidthListThreshold={0}
                            Layout={groupCanvas.canvasLayout}
                            CanvasBackgroundColor='rgba(0,0,0,0)'
                            ElementSettings={{ zDepth: 5 }}
                        />
                    }
                    else {
                        gridJSX = <div
                            key={'row-grid-' + index}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill,minmax(' + widgetContainerWidth + 'px, 1fr))',
                                width: width,
                                clear: 'both',
                                gridRowGap: WidgetMargin.h,
                                gridColumnGap: WidgetMargin.w,
                            }}
                        >
                            {_.map(group.widgets, renderGridItem)}
                        </div>
                    }

                    return [
                        titleJSX,
                        gridJSX
                    ];
                })}
        </div>;
    }
    // ---------------------------------------------------------------------------------------------
    private _getStateFromProps(props: PartialIWidgetThingsProps<DynamicGridData>): DynamicGridState {
        const { WidgetWidth, WidgetHeight, ToolbarIcons } = props.WidgetData;

        return {
            widgetWidth: WidgetWidth,
            widgetHeight: WidgetHeight,
            toolbarIcons: ToolbarIcons,
            groups: null,
            groupCanvas: null
        }
    }
    // ---------------------------------------------------------------------------------------------
    private _updateData(props: PartialIWidgetThingsProps<DynamicGridData>) {
        if (!isDefined(props.PortsStates))
            return;

        // Update internal widgets based on ports events
        this._updateDataFromPort(props, PortKeys.setup);
        this._updateDataFromPort(props, PortKeys.data);
    }
    // ---------------------------------------------------------------------------------------------
    private _updateDataFromPort(
        props: PartialIWidgetThingsProps<DynamicGridData>,
        portKey: string
    ) {
        const portData = props.PortsStates[portKey];
        if (!isDefined(portData))
            return;

        // Check if we have actual new port event
        if (this._previousVersions[portKey] != null &&
            this._previousVersions[portKey] >= portData.Version)
            return;
        this._previousVersions[portKey] = portData.Version;

        // check port key
        if (portKey == PortKeys.setup) {
            let setup: Setup;

            if (portData.LastStateStatus == StateStatus.NoData) {
                setup = _.cloneDeep(props.WidgetDefaultValues[PortKeys.setup]);
                if (!isDefined(setup)) {
                    return;
                }
            }
            else if (portData.LastStateStatus == StateStatus.Valid) {
                setup = _.cloneDeep(portData.State);
            }
            else
                // do nothing
                return;

            let willUpdate = false;
            let newComponentState: Partial<DynamicGridState> = {};
            // override width if specified
            if (isDefined(setup.width) &&
                setup.width != this.state.widgetWidth) {
                willUpdate = true;
                newComponentState.widgetWidth = setup.width;
            }

            // override width if specified
            if (isDefined(setup.height) &&
                setup.height != this.state.widgetHeight) {
                willUpdate = true;
                newComponentState.widgetHeight = setup.height;
            }

            // override toolbar icons if specified
            if (isDefined(setup.toolbarIcons)) {
                willUpdate = true;
                newComponentState.toolbarIcons = setup.toolbarIcons;
            }

            // setup groups (if any)
            if (isDefined(setup.groups)) {
                willUpdate = true;
                newComponentState.groups = setup.groups;
                newComponentState.groupCanvas = null;
            }

            // setup widget (if any)
            if (isDefined(setup.widgets)) {

                // remove all things
                this._webModule.RemoveThings(_.keys(this._webModule.Things), false);

                const _trackUpdates = _.mapValues(
                    this._widgetsSetup,
                    v => v.trackUpdates);

                // re-init widgets
                this._widgets = {};
                this._widgetsOrder = [];
                this._widgetsSetup = {};

                _.forEach(setup.widgets, v => {

                    // try get widget descriptor 
                    const type = DynamicGrid._FriendlyTypeToWidgetType[v.type];
                    if (!isDefined(type) || !_.has(DynamicGrid._WidgetDescriptors, type))
                        return;

                    // get widget
                    let desc = _.cloneDeep(DynamicGrid._WidgetDescriptors[type]);
                    desc.WidgetTitle = v.title;
                    desc.Key += '_' + this._fixWidgetId(v.id);

                    // copy WidgetData (if specified)
                    if (isDefined(v.settings))
                        desc.Data = v.settings;

                    if (!isDefined(v.trackUpdates))
                        v.trackUpdates = _trackUpdates[desc.Key];

                    // Any settings for track updates?
                    if (isDefined(v.trackUpdates)) {
                        v.trackUpdates.enable = v.trackUpdates.enable !== false;
                        desc.TrackUpdates = {
                            ShowLastUpdateTs: v.trackUpdates.enable,
                            DateFormat: v.trackUpdates.dateFormat,
                            ViewFormat: v.trackUpdates.viewFormat,
                            Timestamp: v.trackUpdates.timestamp,
                            IsExplicit: v.trackUpdates.isExplicit
                        };
                    }

                    // Show loading indicator and wait for data?
                    if (isDefined(v.beginInLoadingState))
                        desc.StartWithLoading = v.beginInLoadingState;

                    let widget = Factory.CommonCreateWidgetThing(desc, this._webModule);

                    // merge the override settings
                    widget.Description = _.merge({}, widget.Description, widget.OverrideDescription);

                    // update cache
                    this._widgets[v.id] = widget;
                    // todo: check if we need to clone this element
                    this._widgetsSetup[widget.Description.Key] = v;
                    this._widgetsOrder.push(v.id);
                });

                if (!willUpdate)
                    this.setState(newComponentState as DynamicGridState);

                // Update the callback for outgoing port events.
                DummyWebNode.updatePortEventsOutCb(
                    this.props.ThingKey,
                    this._dummyPortEventOutCB
                );
            }
        } else if (portKey == PortKeys.data) {
            if (portData.LastStateStatus != StateStatus.Valid)
                return;

            let data: Data[] = portData.State;

            _.forEach(data, (el) => {
                if (el.id == null)
                    return;

                // Find instance of widget
                const widget = this._widgets[el.id] as IWidgetThingsClass<any>;
                if (!isDefined(widget))
                    return;

                // Find the setup of the widget
                const setup = this._widgetsSetup[widget.Description.Key];
                if (!isDefined(setup))
                    return;

                // Is timestamp provided?
                const ts = el.timestamp;
                const settings = props.WidgetData.TrackUpdates;

                if (isDefined(setup.trackUpdates)) {
                    // Update timestamp of setup settings.
                    setup.trackUpdates.timestamp = ts;
                } else if (settings.ShowLastUpdateTs) {
                    // Initialize setup tracking.
                    setup.trackUpdates = {
                        enable: true,
                        timestamp: ts,
                        isExplicit: settings.IsExplicit,
                        dateFormat: settings.DateFormat,
                        viewFormat: settings.ViewFormat
                    };
                }

                // try get data port key
                const dataPortKey = widget.GetDataPortKeyStatic();
                if (IsNullOrEmpty(dataPortKey))
                    return;

                // simulate incoming port event
                this._webNode.YodiWS.onPortEvents([
                    {
                        RevNum: -1,
                        Timestamp: moment.utc().unix(),
                        State: el.data,
                        PortKey: `${widget.ThingKey}${PlegmaStatics.PlegmaAPI.KeySeparator}${dataPortKey}`
                    }
                ])
            });
        }
    }
    // ---------------------------------------------------------------------------------------------
    private _fixWidgetId(id: number | string): string {
        return String(id).replace('_', '1').replace('-', '1');
    }
    // ---------------------------------------------------------------------------------------------
    private _dummyPortEventOutCB = (events: Plegma.PortEvent[]) => {

        if (_.isEmpty(this._widgetsSetup)) {
            console.error("Should not be here");
            this.forceUpdate();
            return;
        }

        _.forEach(events, (ev) => {

            // at first, defer utility ports
            // try get port key, the expected format is
            // <WebModule Key>_<DynamicGrid Description Key>_<Inner widget Description Key>-<PortKey>
            // so we just search for '-'
            const portKeyIndex = ev.PortKey.lastIndexOf('-');
            const portKey = ev.PortKey.substring(portKeyIndex + 1);
            if (isWebThingKey(portKey))
                return;

            // try get id of the widget that fired the event
            // 1rst, remove PortKey
            let widgetKey = ev.PortKey.substring(0, portKeyIndex);

            // note that <WebModule Key>_<DynamicGrid Description Key> is equal to this.props.ThingKey !
            widgetKey = widgetKey.replace(this.props.ThingKey + '_', '');

            // if state is a serialized object; try parse
            let state = ev.State;
            if (isString(state)) {
                state = _.trim(state)
                if ((_.startsWith(state, '{') && _.endsWith(state, '}')) || // JSON
                    (_.startsWith(state, '[') && _.endsWith(state, ']')))   // or JSON array
                    try { state = JSON.parse(state); } catch { }
            }

            // feed output port
            const portEvent: WidgetDataJSON = {
                widget: this._widgetsSetup[widgetKey],
                data: state,
                portKey: portKey,
            };
            this.props.SendPortEvent(PortKeys.widgetData, JSON.stringify(portEvent));
        })
    }
    // ---------------------------------------------------------------------------------------------
    private _drawGroupTitle(title: string, titleStyle: SetupGroupStyle): boolean {
        return !IsNullOrEmpty(title) || !IsNullOrEmpty(titleStyle.icon);
    }
    // ---------------------------------------------------------------------------------------------
    private _onCanvasLayoutChanged = (newLayout: CanvasLayout) => {
        this.setState((s: DynamicGridState) => {
            s.groupCanvas.canvasLayout = newLayout;
            s.groupCanvas.hasChanges = true;
            return s;
        });
    }
    // ---------------------------------------------------------------------------------------------
    private onGroupReorderSave = () => {
        this.setState((s: DynamicGridState) => {

            // find widgets order
            const widgetsOrderIds = _.map(Canvas.SortLayoutArray(_.values(s.groupCanvas.canvasLayout.LayoutDesc)), (l) => l.Key);

            // send port event
            this.props.SendPortEvent(PortKeys.groupReorder, JSON.stringify({
                group: s.groups[s.groupCanvas.groupIndex],
                widgetsOrderIds: widgetsOrderIds
            } as GroupReorderJson))

            // update `originalLayoutDesc`
            s.groupCanvas.originalLayoutDesc = _.cloneDeep(s.groupCanvas.canvasLayout.LayoutDesc);
            s.groupCanvas.hasChanges = false;
            return s;
        });

    }
    // ---------------------------------------------------------------------------------------------
    private onGroupReorderCancel = () => {
        this.setState({ groupCanvas: null });
    }
    // ---------------------------------------------------------------------------------------------

}
// =================================================================================================
class DynamicGridSettings extends PureComponent<IWidgetSettingsProps<DynamicGridData>> {
    constructor(props: IWidgetSettingsProps<DynamicGridData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<DynamicGridData>[] = [
            {
                name: '',
                elements: [
                    {
                        type: SettingsType.Slider,
                        label: 'Widget Width',
                        dataPath: 'WidgetWidth',
                        tooltip: 'Widget\'s width in pixels.',
                        minValue: 200,
                        maxValue: 600,
                        stepValue: 10,
                        valuePosfix: 'px'
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Widget Height',
                        dataPath: 'WidgetHeight',
                        tooltip: 'Widget\'s height in pixels.',
                        minValue: 200,
                        maxValue: 600,
                        stepValue: 10,
                        valuePosfix: 'px'
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Widget Margin Width',
                        dataPath: 'WidgetMargin.w',
                        tooltip: 'Minimum horizontal margin in pixels between widgets.',
                        minValue: 0,
                        maxValue: 10,
                        stepValue: 1,
                        valuePosfix: 'px'
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Widget Margin Height',
                        dataPath: 'WidgetMargin.h',
                        tooltip: 'Vertical margin in pixels between widgets.',
                        minValue: 0,
                        maxValue: 10,
                        stepValue: 1,
                        valuePosfix: 'px'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Fill Height',
                        dataPath: 'FillHeight',
                        tooltip: 'If enabled, widgets will be vertically stretched to fill the missing space.',
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        dataPath: 'BackgroundColor',
                        tooltip: 'Widget\'s background color.',
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Shadow zDepth',
                        dataPath: 'ZDepth',
                        tooltip: 'Widget\'s shadow. Greater value adds more shadow effect around the widget.',
                        minValue: 0,
                        maxValue: 5,
                        stepValue: 1,
                        valuePosfix: ''
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Border Radius',
                        dataPath: 'BorderRadius',
                        tooltip: 'Rounds the corners of a widget\'s outer border edge. You can set border radius to make circular corners.',
                        minValue: 0,
                        maxValue: 20,
                        stepValue: 1,
                        valuePosfix: 'px'
                    },
                    {
                        type: SettingsType.Group,
                        label: 'Outline',
                        name: 'Outline',
                        elements: [
                            {
                                type: SettingsType.Slider,
                                isNullable: true,
                                label: 'Thickness',
                                dataPath: 'Outline.Thickness',
                                tooltip: 'The width of line in pixels that is drawn around the widget. To disable outline, set width to 0px.',
                                minValue: 0,
                                maxValue: 8,
                                stepValue: 1,
                                valuePosfix: 'px'
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: 'Color',
                                dataPath: 'Outline.Color',
                                tooltip: 'The color of line that is drawn around the widget.',
                            }
                        ]
                    },
                    {
                        label: 'Toolbar',
                        name: 'Toolbar',
                        type: SettingsType.Group,
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Zoom',
                                dataPath: 'Zoom',
                                tooltip: 'Whether to support widget zoom or not.',
                            },
                            {
                                type: SettingsType.ItemsMgmtDialog,
                                label: 'Toolbar Buttons',
                                tooltip: 'Custom toolbar buttons drawn on widgets\' toolbar.',
                                dataPath: 'ToolbarIcons',
                                itemsInfo: [
                                    {
                                        type: SettingsType.String,
                                        label: 'ID',
                                        dataPath: 'id',
                                        isUnique: true
                                    },
                                    {
                                        type: SettingsType.String,
                                        label: 'Tooltip',
                                        dataPath: 'tooltip'
                                    },
                                    {
                                        type: SettingsType.ExtendedIcon,
                                        label: 'Icon',
                                        dataPath: 'icon',
                                        iconTypePath: 'iconType',
                                        supportUpload: { ext: 'png', imgQuality: 0.9, maxHeight: 64, maxWidth: 64 },
                                        supportUrl: true
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: 'Icon color',
                                        dataPath: 'iconColor',
                                        isEnabled: (data, row: ToolbarIcon) => row.iconType == eIconType.fontAwesome
                                    }
                                ],
                                createNewItem: () => {
                                    return {
                                        icon: '',
                                        tooltip: 'Icon tooltip',
                                        icontype: eIconType.fontAwesome,
                                        id: 'Icon ' + getRandomSmallKey()
                                    } as ToolbarIcon;
                                },
                            },
                            {
                                label: 'Title style',
                                name: 'Title style',
                                type: SettingsType.Group,
                                elements: [
                                    {
                                        type: SettingsType.Icon,
                                        label: 'Icon',
                                        dataPath: 'TitleStyle.Icon',
                                        tooltip: 'Toolbar icon for all widgets\' toolbars.',
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        label: 'Alignment',
                                        dataPath: 'TitleStyle.Alignment',
                                        tooltip: 'Title\'s alignment. Available options: "Center", "Right" and "Left".',
                                        items: [
                                            { value: TitleAlign.Left, label: 'Left' },
                                            { value: TitleAlign.Center, label: 'Center' },
                                            { value: TitleAlign.Right, label: 'Right' }
                                        ],
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: 'Font color',
                                        dataPath: 'TitleStyle.FontColor',
                                        tooltip: 'Title\'s font color.',
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: 'Background color',
                                        dataPath: 'TitleStyle.BackgroundColor',
                                        tooltip: 'Title\'s background color.',
                                    },
                                    {
                                        type: SettingsType.Slider,
                                        label: 'Height',
                                        dataPath: 'TitleStyle.TitleHeight',
                                        minValue: 25,
                                        maxValue: 40,
                                        stepValue: 1,
                                        valuePosfix: 'px'
                                    }
                                ]
                            },
                            {
                                type: SettingsType.Group,
                                name: 'Track Updates',
                                tooltip: 'Customizes the tracking of view updates',
                                elements: [
                                    {
                                        type: SettingsType.Boolean,
                                        label: 'Enable',
                                        dataPath: 'TrackUpdates.ShowLastUpdateTs',
                                        tooltip: 'Keeps track of view updates'
                                    },
                                    {
                                        type: SettingsType.Boolean,
                                        label: 'Explicit',
                                        dataPath: 'TrackUpdates.IsExplicit',
                                        tooltip: 'When enabled, the timestamp needs to be provided by the data (input) port. Otherwise, the last view update (of widget) will be used.',
                                        isHidden: data => !data.TrackUpdates.ShowLastUpdateTs
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        label: 'Date Format',
                                        dataPath: 'TrackUpdates.DateFormat',
                                        tooltip: 'Specifies how the date is represented',
                                        items: [
                                            { value: DateFormat.Short, label: 'Short' },
                                            { value: DateFormat.Long, label: 'Long' },
                                            { value: DateFormat.ISO, label: 'ISO' },
                                            { value: DateFormat.ISOMS, label: 'ISO ms' }
                                        ],
                                        isHidden: data => !data.TrackUpdates.ShowLastUpdateTs
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        label: 'View Format',
                                        dataPath: 'TrackUpdates.ViewFormat',
                                        tooltip: 'Specifies how the date is displayed',
                                        items: [
                                            { value: ITsViewFormat.Tooltip, label: 'Icon tooltip' },
                                            { value: ITsViewFormat.Hover, label: 'Hover tooltip' },
                                            { value: ITsViewFormat.Text, label: 'Text' }
                                        ],
                                        isHidden: data => !data.TrackUpdates.ShowLastUpdateTs
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        label: 'Groups',
                        name: 'Groups',
                        type: SettingsType.Group,
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Support ordering',
                                dataPath: 'GroupsOrdering',
                                tooltip: 'Whether to support runtime ordering in groups or not.'
                            },
                            {
                                label: 'Title Style',
                                name: 'Title Style',
                                type: SettingsType.Group,
                                elements: [
                                    {
                                        type: SettingsType.Icon,
                                        label: 'Icon',
                                        dataPath: 'GroupTitleStyle.icon',
                                        tooltip: 'Toolbar icon for all groups\' toolbars.',
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        label: 'Alignment',
                                        dataPath: 'GroupTitleStyle.alignment',
                                        tooltip: 'Title\'s alignment. Available options: "Center", "Right" and "Left".',
                                        items: [
                                            { value: TitleAlign.Left, label: 'Left' },
                                            { value: TitleAlign.Center, label: 'Center' },
                                            { value: TitleAlign.Right, label: 'Right' }
                                        ],
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: 'Font color',
                                        dataPath: 'GroupTitleStyle.fontColor',
                                        tooltip: 'Title\'s font color.',
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: 'Background color',
                                        dataPath: 'GroupTitleStyle.backgroundColor',
                                        tooltip: 'Title\'s background color.',
                                    },
                                    {
                                        type: SettingsType.Slider,
                                        label: 'Height',
                                        dataPath: 'GroupTitleStyle.titleHeight',
                                        minValue: 25,
                                        maxValue: 40,
                                        stepValue: 1,
                                        valuePosfix: 'px'
                                    }
                                ]
                            },
                            {
                                label: 'Margin',
                                name: 'Margin',
                                tooltip: 'Margins applied when group title is drawn. If no title or icon is specified the margin options below are ignored.',
                                type: SettingsType.Group,
                                elements: [
                                    {
                                        type: SettingsType.Slider,
                                        label: 'Before',
                                        dataPath: 'GroupMargin.Before',
                                        tooltip: 'Vertical margin in pixels from previous grid group. Note: in the first group title, we don\'t apply the margin.',
                                        minValue: 0,
                                        maxValue: 20,
                                        stepValue: 1,
                                        valuePosfix: 'px',
                                    },
                                    {
                                        type: SettingsType.Slider,
                                        label: 'After',
                                        dataPath: 'GroupMargin.After',
                                        tooltip: 'Vertical margin in pixels after group\'s title.',
                                        minValue: 0,
                                        maxValue: 20,
                                        stepValue: 1,
                                        valuePosfix: 'px',
                                    }
                                ]
                            }
                        ]
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =================================================================================================
const enum PortKeys {
    setup = 'setup',
    data = 'data',
    toolbarIcon = 'icon',
    widgetData = 'widget_data',
    groupReorder = 'group_reorder'
}
export class DynamicGridWidget extends IWidgetThingsClass<DynamicGridData> {
    Render(key: string, width: number, height: number): ReactNode {
        return <DynamicGrid />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <DynamicGridSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // Set the Ports
        ports.push(new WebThingPort(
            {
                Name: 'Setup',
                PortKey: PortKeys.setup,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.setup, data))
        );

        ports.push(new WebThingPort(
            {
                Name: 'Data',
                PortKey: PortKeys.data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.data, data))
        );

        ports.push(new WebThingPort(
            {
                Name: 'Toolbar icon selected',
                PortKey: PortKeys.toolbarIcon,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Trigger)
        );

        ports.push(new WebThingPort(
            {
                Name: 'Data from widget',
                PortKey: PortKeys.widgetData,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Trigger)
        );

        ports.push(new WebThingPort(
            {
                Name: 'Data from group reorder',
                PortKey: PortKeys.groupReorder,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Trigger)
        );

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): DynamicGridData {
        return {
            WidgetWidth: 250,
            WidgetHeight: 250,
            WidgetMargin: { w: 4, h: 4 },
            BackgroundColor: '#4A90E2',
            ZDepth: 1,
            BorderRadius: 2,
            FillHeight: false,
            TitleStyle: {
                Alignment: TitleAlign.Left,
                BackgroundColor: 'rgb(255,255,255)',
                FontColor: 'rgb(0,0,0)',
                Icon: '',
                TitleHeight: 25,
                FontAutoSize: true,
                FontSize: 14
            },
            TrackUpdates: {
                ShowLastUpdateTs: false,
                IsExplicit: false,
                DateFormat: DateFormat.Short,
                ViewFormat: ITsViewFormat.Tooltip
            },
            Zoom: false,
            Outline: {
                Thickness: 0,
                Color: 'rgb(189, 189, 189)'
            },
            ToolbarIcons: [],
            GroupTitleStyle: {
                alignment: TitleAlign.Left,
                backgroundColor: 'rgb(255,255,255)',
                fontColor: 'rgb(0,0,0)',
                icon: '',
                titleHeight: 25
            },
            GroupMargin: {
                Before: 6,
                After: 4
            },
            GroupsOrdering: false
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return DynamicGridWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Dynamic Grid',
            WidgetTitle: '',
            Key: 'DynamicGridWidget',
            Type: 'DynamicGridWidget',
            Data: DynamicGridWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: IconURIPath + '/png/dynamic_grid.png'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.setup) {
            return SetupJsonSchemeValidator().DataValidateString(value);
        }
        else if (key == PortKeys.data) {
            return DataJsonSchemeValidator().DataValidateString(value);
        }
        return { valid: true, errors: '' };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: DynamicGridData): DynamicGridData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}